import { FC } from 'react';
import { ICoordinate, MissionFeatureCollection } from '@autopylot-internal/tiles-client';
import { ITimeOfDayAdvisoryProperties, ITimeOfDay, ITimeOfDayEnum } from '@autopylot-internal/tiles-client';
import { AdvisoryCompleteIcon } from '../../../components/icons/Icons.tsx';
import { NoWrap } from '../../../components/typography/NoWrap.tsx';
import { formatLocalTime } from '../../../support/helpers/format.ts';
import { SecondaryAdvisory, SecondaryWarningAdvisory } from '../../SecondaryAdvisory.tsx';
import { AdvisoryLink } from '../AdvisoryLink.tsx';

type ITimeOfDayLabelMap = { [key in ITimeOfDayEnum]: string };

const recreationalBlockedTimeOfDayToLabel: ITimeOfDayLabelMap = {
    'day': '[filtered]',
    'night': 'Night is from',
    'civil-twilight': '[filtered]'
};

const licensedTimeOfDayLabels: ITimeOfDayLabelMap = {
    'day': '[filtered]',
    'night': 'Lights are required at night',
    'civil-twilight': 'Lights are required during civil twilight'
};

const recreationalTimeOfDayLabels: ITimeOfDayLabelMap = {
    'day': '[filtered-day]',
    'night': 'Lights are required at night',
    'civil-twilight': 'Lights are required during civil twilight'
};

const TimePeriod: FC<{period: ITimeOfDay, location: ICoordinate}> = ({ period, location }) => {
    const start = period.startTime ? formatLocalTime({ time: period.startTime, location }) : undefined;
    const end = period.endTime ? formatLocalTime({ time: period.endTime, location }) : undefined;
    if (!start && !end) {
        return 'for the duration of the mission';
    }
    if (!start) {
        return <span> until <NoWrap><b>{end}</b></NoWrap></span>;
    }
    if (!end) {
        return <span> from <NoWrap><b>{start}</b></NoWrap></span>;
    }
    return (
        <span> from <NoWrap><b>{start}</b></NoWrap> to <NoWrap><b>{end}</b></NoWrap></span>
    );
};

const NightAndTwilightTimes = ({ relevantTimesOfDay, mission, prefix = '', timeOfDayLabelMap }: { relevantTimesOfDay: ITimeOfDay[], mission: MissionFeatureCollection, prefix?: string, timeOfDayLabelMap: ITimeOfDayLabelMap }) => {
    const nightAndTwilight = relevantTimesOfDay.filter(p => p.timeOfDay !== 'day');
    return (
        <span>
            <span>{prefix}</span>
            {nightAndTwilight.map((time, i) => (
                <span key={i}> {timeOfDayLabelMap[time.timeOfDay]} <TimePeriod period={time} location={mission.getMissionCenter()} />.</span>
            ))}
        </span>
    );
};

export const DayNightAdvisory = (props: ITimeOfDayAdvisoryProperties & { mission: MissionFeatureCollection }) => {
    // DEPRECATED: recreational, at night, until it applies to everyone
    if (props.advisoryType === 'block') {
        return (
            <SecondaryWarningAdvisory header="Rules for Nighttime Operations" advisoryType="block">
                <span>Flights are prohibited during nighttime hours for Recreational Pilots.</span>
                <NightAndTwilightTimes
                    mission={props.mission}
                    relevantTimesOfDay={props.relevantTimesOfDay.filter(p => p.timeOfDay === 'night')}
                    timeOfDayLabelMap={recreationalBlockedTimeOfDayToLabel}
                />
            </SecondaryWarningAdvisory>
        );
    }

    // if only during the day...
    if (props.relevantTimesOfDay.every(r => r.timeOfDay === 'day')) {
        const daylight = props.relevantTimesOfDay[0];
        return (
            <SecondaryAdvisory header="Rules for Daylight Operations" advisoryType="advise" icon={AdvisoryCompleteIcon} iconColor="success">
                Cleared to fly during daylight hours.
                {daylight && <> Daylight is <TimePeriod period={daylight} location={props.mission.getMissionCenter()} />.</>}
            </SecondaryAdvisory>
        );
    }

    const { isLicensed } = props.mission.getMissionRadius().properties;

    if (isLicensed && props.relevantTimesOfDay.some(r => r.timeOfDay === 'night')) {
        return (
            <SecondaryWarningAdvisory header="Rules for Nighttime Operations" advisoryType="advise">
                <span>Nighttime flights may only be conducted if (1) the operator has completed the required FAA training/testing, and (2) the sUAS is lighted with anti-collision lighting visible for at least 3 statute miles that has a flash rate sufficient to avoid a collision.</span>
                <NightAndTwilightTimes mission={props.mission} relevantTimesOfDay={props.relevantTimesOfDay} timeOfDayLabelMap={licensedTimeOfDayLabels} />
            </SecondaryWarningAdvisory>
        );
    }
    // recreational, includes night
    else if (!isLicensed && props.relevantTimesOfDay.some(r => r.timeOfDay === 'night')){
        return (
            <SecondaryWarningAdvisory header="Rules for Nighttime Operations" advisoryType="advise">
                <span>For nighttime flights, it is recommended that sUAS should be lit with anti-collision lighting visible for at least 3 statute miles away and arrange lights on the UA in such a way that allows recreational flyers to determine the orientation and flight path of the aircraft. For additional guidance, refer to “Night Flight” in <AdvisoryLink url="https://www.faa.gov/documentLibrary/media/Advisory_Circular/AC_91-57D.pdf" text="Advisory Circular 91-57" />.</span>
                <NightAndTwilightTimes mission={props.mission} relevantTimesOfDay={props.relevantTimesOfDay} timeOfDayLabelMap={licensedTimeOfDayLabels} />
            </SecondaryWarningAdvisory>
        );
    }

    // civil twilight (no night), licensed, or recreational
    return (
        <SecondaryWarningAdvisory header="Rules for Civil Twilight Operations" advisoryType="advise">
            <span>Flights during civil twilight require anti-collision lighting to be activated, visible for at least 3 statute miles, and have a flash rate sufficient to avoid a collision.</span>
            <NightAndTwilightTimes mission={props.mission} relevantTimesOfDay={props.relevantTimesOfDay} timeOfDayLabelMap={recreationalTimeOfDayLabels} />
        </SecondaryWarningAdvisory>

    );
};
